/*
 * Lucky Muffin — Harbor fit check (sections/lm-pdp-fitcheck.liquid).
 *
 * Two layers:
 *   - core: pure functions (units, fit logic, SVG builders) with no DOM use.
 *     Exported as window.LMFit and module.exports so scripts/tests (and later
 *     the Amazon size-chart script) run the exact same math as the page.
 *   - page: binds every .lm-fitcheck root, re-binds on theme-editor reloads.
 *
 * All lengths are inches internally. Customer-facing copy comes from the
 * section's JSON payload, so it stays in Liquid where the brand check sees it.
 */
(function (global) {
  'use strict';

  var CM_PER_IN = 2.54;
  var PANEL_W = 30;

  /* ------------------------------------------------------------ core */

  function num(v) {
    if (v === null || v === undefined || String(v).trim() === '') return null;
    var n = parseFloat(String(v).replace(',', '.'));
    return isFinite(n) && n >= 0 ? n : NaN;
  }

  /* parts: { ft, inch } for imperial, { cm } for metric. Returns inches,
     null when nothing was entered, NaN when the entry is not a number. */
  function toInches(parts, units) {
    if (units === 'metric') {
      var cm = num(parts.cm);
      return cm === null || isNaN(cm) ? cm : cm / CM_PER_IN;
    }
    var ft = num(parts.ft);
    var inch = num(parts.inch);
    if (ft === null && inch === null) return null;
    if (isNaN(ft) || isNaN(inch)) return NaN;
    return (ft || 0) * 12 + (inch || 0);
  }

  function fromInches(inches, units) {
    if (inches === null || !isFinite(inches)) return units === 'metric' ? { cm: '' } : { ft: '', inch: '' };
    if (units === 'metric') return { cm: String(Math.round(inches * CM_PER_IN)) };
    var total = Math.round(inches);
    return { ft: String(Math.floor(total / 12)), inch: String(total % 12) };
  }

  /* style 'ftin' → 8′ 6″, 'in' → 60″. Metric is always whole cm. */
  function format(inches, units, style) {
    if (units === 'metric') return Math.round(inches * CM_PER_IN) + ' cm';
    var total = Math.round(inches);
    if (style === 'in' || total < 12) return total + '″';
    var ft = Math.floor(total / 12);
    var rem = total % 12;
    return rem ? ft + '′ ' + rem + '″' : ft + '′';
  }

  function formatDims(w, d, units) {
    if (units === 'metric') return Math.round(w * CM_PER_IN) + ' × ' + Math.round(d * CM_PER_IN) + ' cm';
    return Math.round(w) + '″ × ' + Math.round(d) + '″';
  }

  /* Open floor left beside the pen, by where it sits. The wall runs along the
     room's width (top edge of the plan). */
  function walkwayFor(fx, fy, placement) {
    fx = Math.max(0, fx);
    fy = Math.max(0, fy);
    if (placement === 'corner') return Math.max(fx, fy);
    if (placement === 'open') return Math.max(fx, fy) / 2;
    return Math.max(fy, fx / 2);
  }

  /* room { w, l }, config { w, d }, opts { placement, walkway, tolerance,
     orient: null (auto) | 'normal' | 'rotated' } */
  function evaluateSpace(room, config, opts) {
    opts = opts || {};
    var tol = opts.tolerance || 0;
    var want = opts.walkway || 0;
    var options = [];
    if (opts.orient !== 'rotated') options.push({ a: config.w, b: config.d, rotated: false });
    if (opts.orient !== 'normal' && config.w !== config.d) options.push({ a: config.d, b: config.w, rotated: true });
    if (!options.length) options.push({ a: config.w, b: config.d, rotated: false });

    var best = null;
    options.forEach(function (o) {
      var fx = room.w - o.a;
      var fy = room.l - o.b;
      var r = {
        a: o.a, b: o.b, rotated: o.rotated, fx: fx, fy: fy,
        fits: fx >= -tol && fy >= -tol,
        walkway: walkwayFor(fx, fy, opts.placement),
        overflow: Math.min(fx, fy)
      };
      if (!best ||
          (r.fits && !best.fits) ||
          (r.fits === best.fits && (r.fits ? r.walkway > best.walkway : r.overflow > best.overflow))) {
        best = r;
      }
    });
    best.status = !best.fits ? 'nofit' : best.walkway < want ? 'snug' : 'fits';
    return best;
  }

  /* Largest visible setup that fits comfortably (falls back to snug).
     Ties go to the `recommended` setup, then block order. */
  function suggest(room, configs, opts) {
    function pick(status) {
      var found = null;
      configs.forEach(function (c) {
        if (evaluateSpace(room, c, opts).status !== status) return;
        if (!found) { found = c; return; }
        var area = c.w * c.d;
        var bestArea = found.w * found.d;
        if (area > bestArea || (area === bestArea && c.recommended && !found.recommended)) found = c;
      });
      return found;
    }
    return pick('fits') || pick('snug');
  }

  /* Getting over a barrier is what lets dogs out (from reviews); sofa jumping
     and leaning on the sides aren't asked about. */
  var RANK = { no: 0, once: 1, regular: 2 };

  function sizeFor(shoulder, opts) {
    return shoulder <= opts.okMax ? 'ok' : shoulder <= opts.snugMax ? 'snug' : 'tall';
  }

  /* dog { shoulder, climb: 'no'|'once'|'regular'|null, puppy,
     range: { lo, hi } when the height is a breed's typical one },
     opts { panel, okMax, snugMax }. `spans` flags a breed range that crosses
     a size line, where measuring the dog settles it. */
  function evaluateDog(dog, opts) {
    var size = sizeFor(dog.shoulder, opts);
    var sizeRank = size === 'ok' ? 0 : size === 'snug' ? 1 : 2;
    var climbRank = RANK[dog.climb] || 0;
    var rank = Math.max(sizeRank, climbRank);
    return {
      state: ['ok', 'snug', 'no'][rank],
      size: size,
      climb: dog.climb || null,
      puppy: !!dog.puppy,
      ratio: Math.round((opts.panel / dog.shoulder) * 10) / 10,
      spans: !!dog.range && sizeFor(dog.range.lo, opts) !== sizeFor(dog.range.hi, opts)
    };
  }

  /* ---- breeds: { n: name, lo, hi (adult shoulder, in), a: 'alias, …', mix } */

  function norm(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  }

  function aliasesOf(b) {
    return String(b.a || '').split(',').map(norm).filter(Boolean);
  }

  /* Breeds matching typed text, best first: the exact name or a nickname
     ("lab"), the name starts with it, a word of the name starts with it, an
     alias starts with it, then anywhere. */
  function matchBreeds(breeds, query) {
    var q = norm(query);
    if (!q) return breeds.slice();
    var scored = [];
    breeds.forEach(function (b, i) {
      var name = norm(b.n);
      var aliases = aliasesOf(b);
      function starts(s) { return s.indexOf(q) === 0; }
      var score = name === q || aliases.indexOf(q) > -1 ? -0.5
        : starts(name) ? 0
        : name.split(' ').some(starts) ? 1
        : aliases.some(starts) ? 2
        : name.indexOf(q) > -1 || aliases.some(function (a) { return a.indexOf(q) > -1; }) ? 3
        : -1;
      if (score !== -1) scored.push({ b: b, s: score, i: i });
    });
    scored.sort(function (x, y) { return x.s - y.s || x.i - y.i; });
    return scored.map(function (x) { return x.b; });
  }

  /* Breed for text typed but not picked: an exact name, or an alias only one
     breed uses ("frenchie", not "doodle"). */
  function findBreed(breeds, text) {
    var q = norm(text);
    if (!q) return null;
    var byAlias = [];
    for (var i = 0; i < breeds.length; i++) {
      if (norm(breeds[i].n) === q) return breeds[i];
      if (aliasesOf(breeds[i]).indexOf(q) > -1) byAlias.push(breeds[i]);
    }
    return byAlias.length === 1 ? byAlias[0] : null;
  }

  function breedMid(b) {
    return (b.lo + b.hi) / 2;
  }

  /* 9½–11½″ or 24–29 cm */
  function formatRange(lo, hi, units) {
    if (units === 'metric') return Math.round(lo * CM_PER_IN) + '–' + Math.round(hi * CM_PER_IN) + ' cm';
    function half(v) {
      var r = Math.round(v * 2) / 2;
      return Math.floor(r) + (r % 1 ? '½' : '');
    }
    return half(lo) + '–' + half(hi) + '″';
  }

  function fill(template, tokens) {
    return String(template || '').replace(/\{(\w+)\}/g, function (m, k) {
      return Object.prototype.hasOwnProperty.call(tokens, k) ? tokens[k] : m;
    });
  }

  function esc(s) {
    return String(s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function n2(v) { return Math.round(v * 100) / 100; }

  function line(x1, y1, x2, y2, cls) {
    return '<line class="' + cls + '" x1="' + n2(x1) + '" y1="' + n2(y1) + '" x2="' + n2(x2) + '" y2="' + n2(y2) + '" vector-effect="non-scaling-stroke"/>';
  }

  function text(x, y, size, s, cls, extra) {
    return '<text class="' + cls + '" x="' + n2(x) + '" y="' + n2(y) + '" font-size="' + n2(size) + '" text-anchor="middle" dominant-baseline="middle"' + (extra || '') + '>' + esc(s) + '</text>';
  }

  /* Pen rectangle with a joint mark every ~30″ panel along each side. */
  function penShape(x, y, a, b, status) {
    var out = '<rect class="lm-fitcheck__svg-pen lm-fitcheck__svg-pen--' + (status || 'idle') + '" x="' + n2(x) + '" y="' + n2(y) + '" width="' + n2(a) + '" height="' + n2(b) + '" vector-effect="non-scaling-stroke"/>';
    var tick = Math.min(a, b) * 0.04 + 0.5;
    var nx = Math.max(1, Math.round(a / PANEL_W));
    var ny = Math.max(1, Math.round(b / PANEL_W));
    for (var i = 1; i < nx; i++) {
      var px = x + (a * i) / nx;
      out += line(px, y - tick, px, y + tick, 'lm-fitcheck__svg-joint') + line(px, y + b - tick, px, y + b + tick, 'lm-fitcheck__svg-joint');
    }
    for (var j = 1; j < ny; j++) {
      var py = y + (b * j) / ny;
      out += line(x - tick, py, x + tick, py, 'lm-fitcheck__svg-joint') + line(x + a - tick, py, x + a + tick, py, 'lm-fitcheck__svg-joint');
    }
    return out;
  }

  /* Dimension line with end stops and a centred label. */
  function dim(x1, y1, x2, y2, label, size, cls) {
    var vertical = Math.abs(x2 - x1) < Math.abs(y2 - y1);
    var s = size * 0.5;
    var out = line(x1, y1, x2, y2, cls);
    out += vertical
      ? line(x1 - s, y1, x1 + s, y1, cls) + line(x2 - s, y2, x2 + s, y2, cls)
      : line(x1, y1 - s, x1, y1 + s, cls) + line(x2, y2 - s, x2, y2 + s, cls);
    var mx = (x1 + x2) / 2;
    var my = (y1 + y2) / 2;
    var extra = vertical ? ' transform="rotate(-90 ' + n2(mx) + ' ' + n2(my) + ')"' : '';
    out += text(mx, my, size, label, 'lm-fitcheck__svg-dimlabel', extra);
    return out;
  }

  /* Top-down plan, to scale. opts { room|null, pen {a,b}, placement, status,
     walkwayIn, units, label } */
  function planSvg(opts) {
    var pen = opts.pen;
    var units = opts.units;
    var room = opts.room;
    var W = room ? room.w : pen.a;
    var L = room ? room.l : pen.b;
    var span = Math.max(W, L, pen.a, pen.b);
    var fs = span * 0.042;
    var m = fs * 3.2;
    var minX = -m;
    var minY = -m;
    var vbW = Math.max(W, pen.a) + m * 2;
    var vbH = Math.max(L, pen.b) + m * 2;
    var out = '';

    var px = 0;
    var py = 0;
    if (room) {
      if (opts.placement === 'wall' || opts.placement === 'open') px = (W - pen.a) / 2;
      if (opts.placement === 'open') py = (L - pen.b) / 2;
      px = Math.max(0, px);
      py = Math.max(0, py);

      var step = units === 'metric' ? 50 / CM_PER_IN : 12;
      out += '<rect class="lm-fitcheck__svg-room" x="0" y="0" width="' + n2(W) + '" height="' + n2(L) + '" vector-effect="non-scaling-stroke"/>';
      for (var gx = step; gx < W - 0.01; gx += step) out += line(gx, 0, gx, L, 'lm-fitcheck__svg-grid');
      for (var gy = step; gy < L - 0.01; gy += step) out += line(0, gy, W, gy, 'lm-fitcheck__svg-grid');
      out += penShape(px, py, pen.a, pen.b, opts.status);
      out += '<rect class="lm-fitcheck__svg-wall" x="0" y="0" width="' + n2(W) + '" height="' + n2(L) + '" fill="none" vector-effect="non-scaling-stroke"/>';
      if (opts.placement === 'wall' || opts.placement === 'corner') {
        out += line(0, 0, W, 0, 'lm-fitcheck__svg-wallline');
        if (opts.placement === 'corner') out += line(0, 0, 0, L, 'lm-fitcheck__svg-wallline');
      }
      out += dim(0, -m * 0.5, W, -m * 0.5, format(W, units, 'ftin'), fs, 'lm-fitcheck__svg-dim');
      out += dim(-m * 0.5, 0, -m * 0.5, L, format(L, units, 'ftin'), fs, 'lm-fitcheck__svg-dim');

      /* Mark the open strip that sets the walkway figure. */
      if (opts.status !== 'nofit' && opts.walkwayIn > 0.5) {
        var fx = W - pen.a;
        var fy = L - pen.b;
        var wl = format(opts.walkwayIn, units, 'ftin');
        var useY = opts.placement === 'corner' ? fy >= fx
          : opts.placement === 'open' ? fy >= fx
          : fy >= fx / 2;
        /* Off-centre, so the line doesn't sit on a panel joint. */
        if (useY) {
          var y1 = py + pen.b;
          out += dim(px + pen.a * 0.25, y1, px + pen.a * 0.25, y1 + opts.walkwayIn, wl, fs, 'lm-fitcheck__svg-walk');
        } else {
          var x1 = px + pen.a;
          out += dim(x1, py + pen.b * 0.25, x1 + opts.walkwayIn, py + pen.b * 0.25, wl, fs, 'lm-fitcheck__svg-walk');
        }
      }
    } else {
      out += penShape(0, 0, pen.a, pen.b, 'idle');
      out += dim(0, -m * 0.5, pen.a, -m * 0.5, format(pen.a, units, 'in'), fs, 'lm-fitcheck__svg-dim');
      out += dim(-m * 0.5, 0, -m * 0.5, pen.b, format(pen.b, units, 'in'), fs, 'lm-fitcheck__svg-dim');
    }

    var labelSize = Math.min(fs, pen.a / 6, pen.b / 3);
    if (labelSize > fs * 0.55) {
      out += text(px + pen.a / 2, py + pen.b / 2, labelSize, formatDims(pen.a, pen.b, units), 'lm-fitcheck__svg-penlabel');
    }

    return '<svg class="lm-fitcheck__svg" viewBox="' + [n2(minX), n2(minY), n2(vbW), n2(vbH)].join(' ') +
      '" role="img" aria-label="' + esc(opts.label || '') + '" preserveAspectRatio="xMidYMid meet">' + out + '</svg>';
  }

  /* Simple dog side profile, shoulder at height h, rear at x0, ground at g. */
  function dogShape(x0, g, h) {
    function r(x, y, w, hh, rx) {
      return '<rect x="' + n2(x) + '" y="' + n2(y) + '" width="' + n2(w) + '" height="' + n2(hh) + '" rx="' + n2(rx) + '"/>';
    }
    return '<g class="lm-fitcheck__svg-dog">' +
      '<path class="lm-fitcheck__svg-dogtail" d="M' + n2(x0 + 0.05 * h) + ' ' + n2(g - 0.9 * h) + ' Q' + n2(x0 - 0.2 * h) + ' ' + n2(g - 0.95 * h) + ' ' + n2(x0 - 0.22 * h) + ' ' + n2(g - 1.2 * h) + '" fill="none" stroke-width="' + n2(0.07 * h) + '" stroke-linecap="round"/>' +
      r(x0 + 0.08 * h, g - 0.62 * h, 0.15 * h, 0.62 * h, 0.06 * h) +
      r(x0 + 0.86 * h, g - 0.62 * h, 0.15 * h, 0.62 * h, 0.06 * h) +
      r(x0, g - h, 1.1 * h, 0.44 * h, 0.2 * h) +
      r(x0 + 0.9 * h, g - 1.22 * h, 0.22 * h, 0.4 * h, 0.08 * h) +
      '<circle cx="' + n2(x0 + 1.06 * h) + '" cy="' + n2(g - 1.24 * h) + '" r="' + n2(0.2 * h) + '"/>' +
      '<ellipse cx="' + n2(x0 + 1.28 * h) + '" cy="' + n2(g - 1.18 * h) + '" rx="' + n2(0.14 * h) + '" ry="' + n2(0.09 * h) + '"/>' +
      '<ellipse class="lm-fitcheck__svg-dogear" cx="' + n2(x0 + 0.98 * h) + '" cy="' + n2(g - 1.3 * h) + '" rx="' + n2(0.07 * h) + '" ry="' + n2(0.15 * h) + '" transform="rotate(-15 ' + n2(x0 + 0.98 * h) + ' ' + n2(g - 1.3 * h) + ')"/>' +
      '</g>';
  }

  /* Side view: one panel beside the dog, both to scale. opts { panel,
     shoulder|null, units, state, label } */
  function sideSvg(opts) {
    var P = opts.panel;
    var H = opts.shoulder;
    var units = opts.units;
    var dogTop = H ? H * 1.45 : 0;
    var top = Math.max(P, dogTop);
    var fs = Math.max(P, top) * 0.075;
    var m = fs * 2.6;
    var panelW = PANEL_W;
    var gap = H ? Math.max(6, H * 0.45) : 0;
    var dogX = panelW + gap + (H ? 0.25 * H : 0);
    var right = H ? dogX + 1.45 * H : panelW;
    var g = top;
    var out = '';

    out += line(-m, g, right + m, g, 'lm-fitcheck__svg-ground');
    out += '<g class="lm-fitcheck__svg-panel">';
    out += '<rect x="0" y="' + n2(g - P) + '" width="' + panelW + '" height="' + n2(P) + '" fill="none" vector-effect="non-scaling-stroke"/>';
    for (var bx = 3; bx < panelW - 1; bx += 3) out += line(bx, g - P + 1.5, bx, g - 1.5, 'lm-fitcheck__svg-bar');
    out += line(0, g - P + 1.5, panelW, g - P + 1.5, 'lm-fitcheck__svg-bar');
    out += line(0, g - 1.5, panelW, g - 1.5, 'lm-fitcheck__svg-bar');
    out += '</g>';
    out += dim(-m * 0.5, g - P, -m * 0.5, g, format(P, units, 'in'), fs, 'lm-fitcheck__svg-dim');

    if (H) {
      out += '<g class="lm-fitcheck__svg-dogwrap lm-fitcheck__svg-dogwrap--' + (opts.state || 'ok') + '">' + dogShape(dogX, g, H) + '</g>';
      out += line(panelW, g - P, right + m * 0.2, g - P, 'lm-fitcheck__svg-rail');
      out += dim(right + m * 0.5, g - H, right + m * 0.5, g, format(H, units, 'in'), fs, 'lm-fitcheck__svg-dim');
    }

    var vbW = right + m * 2;
    var vbH = top + m * 1.4;
    return '<svg class="lm-fitcheck__svg lm-fitcheck__svg--side" viewBox="' + [n2(-m), n2(-m), n2(vbW), n2(vbH)].join(' ') +
      '" role="img" aria-label="' + esc(opts.label || '') + '" preserveAspectRatio="xMidYMid meet">' + out + '</svg>';
  }

  var core = {
    toInches: toInches,
    fromInches: fromInches,
    format: format,
    formatDims: formatDims,
    walkwayFor: walkwayFor,
    evaluateSpace: evaluateSpace,
    suggest: suggest,
    sizeFor: sizeFor,
    evaluateDog: evaluateDog,
    matchBreeds: matchBreeds,
    findBreed: findBreed,
    breedMid: breedMid,
    formatRange: formatRange,
    fill: fill,
    planSvg: planSvg,
    sideSvg: sideSvg
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = core;
  global.LMFit = core;

  /* ------------------------------------------------------------ page */

  if (typeof document === 'undefined') return;

  var instances = {};

  /* dataLayer + Shopify.analytics.publish. GA4 gets an event only if its name
     is in EVENTS in scripts/ga4-custom-pixel.js. */
  function track(name, data, designMode) {
    if (designMode) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: name }, data));
    try {
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        window.Shopify.analytics.publish(name, data);
      }
    } catch (e) {}
  }

  function init(root) {
    if (!root || root.dataset.lmInit) return;
    var dataEl = root.querySelector('[data-lm-fit-data]');
    if (!dataEl) return;
    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    root.dataset.lmInit = '1';

    var t = data.text;
    var configs = data.configs.filter(function (c) { return c.w > 0 && c.d > 0; });
    var designMode = data.designMode || !!(window.Shopify && window.Shopify.designMode);
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var cleanups = [];

    function q(sel) { return root.querySelector(sel); }
    function qa(sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
    function on(el, ev, fn, opt) {
      if (!el) return;
      el.addEventListener(ev, fn, opt);
      cleanups.push(function () { el.removeEventListener(ev, fn, opt); });
    }

    var state = {
      units: data.units,
      placement: 'wall',
      selected: null,
      orient: null,
      breed: null,
      breedUnknown: false,
      heightSource: null,
      lastResultKey: '',
      lastDogKey: ''
    };

    function byId(id) {
      for (var i = 0; i < configs.length; i++) if (configs[i].id === id) return configs[i];
      return null;
    }
    function byVariant(vid) {
      for (var i = 0; i < configs.length; i++) if (String(configs[i].variantId) === String(vid)) return configs[i];
      return null;
    }
    function defaultConfig() {
      var checked = document.querySelector('input[data-lm-variant]:checked');
      var c = checked && byVariant(checked.value);
      if (c) return c;
      for (var i = 0; i < configs.length; i++) if (configs[i].recommended) return configs[i];
      return configs[0] || null;
    }
    state.selected = defaultConfig();

    /* ---- tabs (mode "both") ---- */
    var tabs = qa('[data-lm-fit-tab]');
    function showTab(name, focus) {
      tabs.forEach(function (tab) {
        var active = tab.dataset.lmFitTab === name;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
        var panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !active;
        if (active && focus) tab.focus();
      });
    }
    tabs.forEach(function (tab, i) {
      on(tab, 'click', function () { showTab(tab.dataset.lmFitTab); });
      on(tab, 'keydown', function (e) {
        var dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (!dir) return;
        e.preventDefault();
        var next = tabs[(i + dir + tabs.length) % tabs.length];
        showTab(next.dataset.lmFitTab, true);
      });
    });

    /* ---- units ---- */
    function readLen(key) {
      return toInches({
        ft: (q('[data-lm-fit-input="' + key + '-ft"]') || {}).value,
        inch: (q('[data-lm-fit-input="' + key + '-in"]') || {}).value,
        cm: (q('[data-lm-fit-input="' + key + '-cm"]') || {}).value
      }, state.units);
    }
    function writeLen(key, inches) {
      var parts = fromInches(inches, state.units);
      Object.keys(parts).forEach(function (k) {
        var el = q('[data-lm-fit-input="' + key + '-' + (k === 'inch' ? 'in' : k) + '"]');
        if (el) el.value = parts[k];
      });
    }
    function readShoulder() {
      var el = q('[data-lm-fit-input="shoulder"]');
      var v = el ? num(el.value) : null;
      if (v === null || isNaN(v)) return v;
      return state.units === 'metric' ? v / CM_PER_IN : v;
    }
    /* Inches → the shoulder field's value in the current units. */
    function shoulderValue(inches) {
      return state.units === 'metric' ? Math.round(inches * CM_PER_IN) : Math.round(inches * 2) / 2;
    }
    function applyUnits(units) {
      var w = readLen('width');
      var l = readLen('length');
      var s = readShoulder();
      state.units = units;
      root.dataset.units = units;
      qa('[data-lm-fit-units-only]').forEach(function (el) { el.hidden = el.dataset.lmFitUnitsOnly !== units; });
      qa('[data-lm-fit-shoulder-unit]').forEach(function (el) { el.textContent = units === 'metric' ? 'cm' : 'in'; });
      if (w !== null && isFinite(w)) writeLen('width', w);
      if (l !== null && isFinite(l)) writeLen('length', l);
      var se = q('[data-lm-fit-input="shoulder"]');
      if (se && s !== null && isFinite(s)) se.value = shoulderValue(s);
      qa('[data-lm-fit-dims]').forEach(function (el) {
        var c = byId(el.dataset.lmFitDims);
        if (c) el.textContent = formatDims(c.w, c.d, units);
      });
    }
    qa('[data-lm-fit-units]').forEach(function (radio) {
      radio.checked = radio.value === state.units;
      on(radio, 'change', function () {
        if (!radio.checked) return;
        applyUnits(radio.value);
        renderSpace(true);
        renderDog(true);
      });
    });

    /* ---- space ---- */
    var planEl = q('[data-lm-fit-plan]');
    var resultEl = q('[data-lm-fit-result]');
    var titleEl = q('[data-lm-fit-title]');
    var textEl = q('[data-lm-fit-text]');
    var suggestEl = q('[data-lm-fit-suggest]');
    var suggestText = q('[data-lm-fit-suggest-text]');
    var suggestBtn = q('[data-lm-fit-suggest-btn]');
    var noneEl = q('[data-lm-fit-none]');
    var ctaBtn = q('[data-lm-fit-cta]');
    var rotateBtn = q('[data-lm-fit-rotate]');
    var live = q('[data-lm-fit-live]');
    var widthErr = q('[data-lm-fit-error="space"]');

    function opts() {
      return { placement: state.placement, walkway: data.walkway, tolerance: data.tolerance, orient: null };
    }

    function room() {
      var w = readLen('width');
      var l = readLen('length');
      if (w === null || l === null) return { empty: true };
      if (isNaN(w) || isNaN(l) || w <= 0 || l <= 0) return { invalid: true };
      return { w: w, l: l };
    }

    var liveTimer = null;
    function announce(msg) {
      if (!live) return;
      clearTimeout(liveTimer);
      liveTimer = setTimeout(function () { live.textContent = msg; }, 700);
    }

    /* Every fit event carries fit_product so GA4 can split Harbor, Haven, Flex. */
    function fitTrack(name, fields) {
      track(name, Object.assign({ fit_product: data.product || '' }, fields), designMode);
    }

    var trackTimer = null;
    function trackLater(fn) {
      clearTimeout(trackTimer);
      trackTimer = setTimeout(fn, 1500);
    }

    function setInvalid(bad, el, keys) {
      keys.forEach(function (k) {
        qa('[data-lm-fit-input^="' + k + '"]').forEach(function (inp) {
          if (bad) inp.setAttribute('aria-invalid', 'true');
          else inp.removeAttribute('aria-invalid');
        });
      });
      if (el) el.hidden = !bad;
    }

    function renderSpace(quiet) {
      var c = state.selected;
      if (!c || !planEl) return;
      var r = room();
      setInvalid(!!r.invalid, widthErr, ['width', 'length']);

      qa('[data-lm-fit-chip]').forEach(function (chip) {
        var cc = byId(chip.dataset.lmFitChip);
        if (!cc || r.empty || r.invalid) { chip.hidden = true; return; }
        var st = evaluateSpace(r, cc, opts()).status;
        chip.hidden = false;
        chip.dataset.status = st;
        chip.textContent = t.chip[st];
      });
      qa('[data-lm-fit-setup]').forEach(function (radio) { radio.checked = radio.value === c.id; });

      var dims = formatDims(c.w, c.d, state.units);
      var tokens = { setup: c.name, dims: dims };

      if (r.empty || r.invalid) {
        planEl.innerHTML = planSvg({ room: null, pen: { a: c.w, b: c.d }, units: state.units, label: fill(t.planEmptyLabel, tokens) });
        resultEl.dataset.status = 'idle';
        titleEl.textContent = '';
        titleEl.hidden = true;
        textEl.textContent = t.emptyText;
        suggestEl.hidden = true;
        noneEl.hidden = true;
        if (rotateBtn) rotateBtn.hidden = true;
        updateCta(c);
        return;
      }

      var o = opts();
      o.orient = state.orient;
      var res = evaluateSpace(r, c, o);
      tokens.walkway = format(res.walkway, state.units, 'ftin');

      planEl.innerHTML = planSvg({
        room: r, pen: { a: res.a, b: res.b }, placement: state.placement, status: res.status,
        walkwayIn: res.walkway, units: state.units,
        label: fill(t.planLabel, {
          room: format(r.w, state.units, 'ftin') + ' × ' + format(r.l, state.units, 'ftin'),
          setup: c.name, dims: formatDims(res.a, res.b, state.units),
          place: t.place[state.placement], walkway: tokens.walkway
        })
      });

      resultEl.dataset.status = res.status;
      titleEl.hidden = false;
      titleEl.textContent = t[res.status + 'Title'];
      textEl.textContent = fill(t[res.status + 'Text'], tokens);
      if (rotateBtn) rotateBtn.hidden = c.w === c.d;

      var s = null;
      if (res.status !== 'fits') {
        s = suggest(r, configs, opts());
        if (s && s.id === c.id) s = null;
      }
      suggestEl.hidden = !s;
      if (s) {
        suggestText.textContent = fill(t.suggestText, { setup: s.name, dims: formatDims(s.w, s.d, state.units) });
        suggestBtn.textContent = fill(t.suggestBtn, { setup: s.name });
        suggestBtn.dataset.target = s.id;
      }
      noneEl.hidden = !(res.status === 'nofit' && !s);

      updateCta(c);

      if (!quiet) {
        announce(titleEl.textContent + '. ' + textEl.textContent + (s ? ' ' + suggestText.textContent : ''));
        var key = [c.id, Math.round(r.w), Math.round(r.l), state.placement, res.status].join('|');
        if (key !== state.lastResultKey) {
          trackLater(function () {
            state.lastResultKey = key;
            fitTrack('lm_fit_result', {
              fit_setup: c.name, fit_room_w_in: Math.round(r.w), fit_room_l_in: Math.round(r.l),
              fit_units: state.units, fit_placement: state.placement, fit_result: res.status,
              fit_walkway_in: Math.round(res.walkway), fit_suggested_setup: s ? s.name : ''
            });
          });
        }
      }
    }

    function updateCta(c) {
      if (!ctaBtn) return;
      ctaBtn.hidden = !c.variantId;
      /* Don't push a setup the space can't take; the suggestion leads instead. */
      ctaBtn.classList.toggle('button--secondary', resultEl.dataset.status === 'nofit');
      var available = c.available !== false;
      ctaBtn.disabled = !available;
      ctaBtn.textContent = available ? fill(t.ctaLabel, { setup: c.name }) : fill(t.ctaSoldOut, { setup: c.name });
    }

    function select(id, keepOrient) {
      var c = byId(id);
      if (!c) return;
      state.selected = c;
      if (!keepOrient) state.orient = null;
      renderSpace();
    }

    qa('[data-lm-fit-input]').forEach(function (inp) {
      var isDog = inp.dataset.lmFitInput === 'shoulder';
      on(inp, 'input', function () {
        if (!isDog) { renderSpace(); return; }
        /* A typed height wins over a breed's typical one. */
        state.heightSource = inp.value.trim() ? 'measured' : null;
        renderDog();
      });
    });
    qa('[data-lm-fit-place]').forEach(function (radio) {
      if (radio.checked) state.placement = radio.value;
      on(radio, 'change', function () {
        if (!radio.checked) return;
        state.placement = radio.value;
        renderSpace();
      });
    });
    qa('[data-lm-fit-setup]').forEach(function (radio) {
      on(radio, 'change', function () { if (radio.checked) select(radio.value); });
    });
    on(suggestBtn, 'click', function () {
      select(suggestBtn.dataset.target);
      var radio = q('[data-lm-fit-setup][value="' + suggestBtn.dataset.target + '"]');
      if (radio) radio.focus();
    });
    on(rotateBtn, 'click', function () {
      var r = room();
      if (r.empty || r.invalid || !state.selected) return;
      var o = opts();
      o.orient = state.orient;
      var cur = evaluateSpace(r, state.selected, o);
      state.orient = cur.rotated ? 'normal' : 'rotated';
      renderSpace();
    });

    on(ctaBtn, 'click', function () {
      var c = state.selected;
      if (!c || !c.variantId) return;
      fitTrack('lm_fit_select', { fit_setup: c.name, fit_result: resultEl.dataset.status || 'idle' });
      var radio = document.querySelector('input[data-lm-variant][value="' + c.variantId + '"]');
      if (!radio) {
        window.location.search = '?variant=' + encodeURIComponent(c.variantId);
        return;
      }
      if (radio.disabled) return;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      var box = radio.closest('.lm-product__configs') || radio;
      box.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      try { radio.focus({ preventScroll: true }); } catch (e) { radio.focus(); }
    });

    /* Follow the buy box when the shopper changes variant there. */
    on(document, 'change', function (e) {
      var target = e.target;
      if (!target || !target.matches || !target.matches('input[data-lm-variant]') || !target.checked) return;
      var c = byVariant(target.value);
      if (c && (!state.selected || c.id !== state.selected.id)) {
        state.selected = c;
        state.orient = null;
        renderSpace(true);
      }
    });

    /* ---- dog ---- */
    var sideEl = q('[data-lm-fit-side]');
    var dogResult = q('[data-lm-fit-dog-result]');
    var dogTitle = q('[data-lm-fit-dog-title]');
    var dogText = q('[data-lm-fit-dog-text]');
    var dogAlt = q('[data-lm-fit-dog-alt]');
    var dogErr = q('[data-lm-fit-error="dog"]');

    function climbValue() {
      var c = q('[data-lm-fit-climb]:checked');
      return c ? c.value : null;
    }

    function renderBreedInfo() {
      if (!breedInfo) return;
      var b = state.breed;
      if (b) {
        /* One nudge to measure: the mix note, or the "enter yours" line
           until they do. */
        var nudge = state.heightSource === 'measured' ? '' : b.mix ? t.breedMixText : t.breedMeasuredText;
        breedInfo.textContent = fill(t.breedInfo, { breed: b.n, range: formatRange(b.lo, b.hi, state.units) }) +
          (nudge ? ' ' + nudge : '');
      } else {
        breedInfo.textContent = state.breedUnknown ? t.breedUnknownText : t.breedHelp;
      }
      breedInfo.hidden = !breedInfo.textContent;
    }

    function renderDog(quiet) {
      if (!sideEl) return;
      renderBreedInfo();
      var sh = readShoulder();
      var invalid = sh !== null && (isNaN(sh) || sh <= 0);
      setInvalid(invalid, dogErr, ['shoulder']);
      var height = format(data.panel, state.units, 'in');

      if (sh === null || invalid) {
        sideEl.innerHTML = sideSvg({ panel: data.panel, shoulder: null, units: state.units, label: fill(t.sideEmptyLabel, { height: height }) });
        dogResult.dataset.status = 'idle';
        dogTitle.hidden = true;
        dogTitle.textContent = '';
        dogText.textContent = t.dogEmptyText;
        if (dogAlt) dogAlt.hidden = true;
        return;
      }

      var puppyEl = q('[data-lm-fit-puppy]');
      var b = state.breed;
      var fromBreed = !!b && state.heightSource === 'breed';
      var dogOpts = { panel: data.panel, okMax: data.dogOkMax, snugMax: data.dogSnugMax };
      var res = evaluateDog({
        shoulder: sh, climb: climbValue(), puppy: puppyEl && puppyEl.checked,
        range: fromBreed ? { lo: b.lo, hi: b.hi } : null
      }, dogOpts);
      var tokens = { height: height, ratio: res.ratio.toFixed(1), shoulder: format(sh, state.units, 'in') };
      var parts = [fill(t.dogRatioText, tokens), t.dogSize[res.size]];
      if (res.spans && !b.mix) parts.push(t.dogSpansText);
      /* Climbing adds a line only when it changes the picture: it outranks
         size, or it's "regular" (a different reason than height alone). */
      var sizeRank = { ok: 0, snug: 1, tall: 2 }[res.size];
      if (res.climb === 'regular' || (res.climb === 'once' && sizeRank < 1)) parts.push(t.dogClimb[res.climb]);
      if (res.puppy) {
        /* With a breed, say how Harbor suits them grown: the result already
           is that when the height came from the breed. */
        var bt = b ? { breed: b.n, range: formatRange(b.lo, b.hi, state.units), adult: t.dogAdult[sizeFor(breedMid(b), dogOpts)] } : null;
        parts.push(!b ? t.dogPuppyText : fill(fromBreed ? t.dogPuppyAdultText : t.dogPuppyBreedText, bt));
      }

      sideEl.innerHTML = sideSvg({
        panel: data.panel, shoulder: sh, units: state.units, state: res.state,
        label: fill(t.sideLabel, tokens)
      });
      dogResult.dataset.status = res.state;
      dogTitle.hidden = false;
      dogTitle.textContent = t.dogTitle[res.state];
      dogText.textContent = parts.filter(Boolean).join(' ');
      if (dogAlt) dogAlt.hidden = res.state !== 'no';

      if (!quiet) {
        announce(dogTitle.textContent + '. ' + dogText.textContent);
        /* Only list names reach analytics, never free text. */
        var breedName = b ? b.n : state.breedUnknown ? 'not_sure' : '';
        var source = fromBreed ? 'breed' : 'measured';
        var key = [Math.round(sh), res.climb, res.puppy, res.state, breedName, source].join('|');
        if (key !== state.lastDogKey) {
          trackLater(function () {
            state.lastDogKey = key;
            fitTrack('lm_fit_dog_result', {
              fit_shoulder_in: Math.round(sh), fit_climbs: res.climb || '', fit_puppy: res.puppy, fit_result: res.state,
              fit_breed: breedName, fit_height_source: source
            });
          });
        }
      }
    }

    qa('[data-lm-fit-climb], [data-lm-fit-puppy]').forEach(function (el) {
      on(el, 'change', function () { renderDog(); });
    });

    /* ---- breed picker: an ARIA combobox with a filtered listbox ---- */
    var breeds = (data.breeds || []).slice().sort(function (x, y) { return x.n.localeCompare(y.n); });
    var breedInput = q('[data-lm-fit-breed]');
    var breedList = q('[data-lm-fit-breeds]');
    var breedInfo = q('[data-lm-fit-breed-info]');
    var shoulderInput = q('[data-lm-fit-input="shoulder"]');
    var measureHelp = q('[data-lm-fit-measure]');
    var shown = [];
    var active = -1;

    function setActive(i) {
      var items = breedList.children;
      if (items[active]) items[active].setAttribute('aria-selected', 'false');
      active = i;
      var el = items[i];
      if (!el) { breedInput.removeAttribute('aria-activedescendant'); return; }
      el.setAttribute('aria-selected', 'true');
      breedInput.setAttribute('aria-activedescendant', el.id);
      /* Scroll within the list only, never the page. */
      if (el.offsetTop < breedList.scrollTop) breedList.scrollTop = el.offsetTop;
      else if (el.offsetTop + el.offsetHeight > breedList.scrollTop + breedList.clientHeight) {
        breedList.scrollTop = el.offsetTop + el.offsetHeight - breedList.clientHeight;
      }
    }

    function openList() {
      /* Reopening after a pick lists every breed, not just the one picked.
         null is the "mixed or not sure" row, always last. */
      var picked = state.breedUnknown || (state.breed && breedInput.value === state.breed.n);
      shown = matchBreeds(breeds, picked ? '' : breedInput.value).concat([null]);
      breedList.textContent = '';
      shown.forEach(function (b, i) {
        var li = document.createElement('li');
        li.id = breedList.id + '-' + i;
        li.className = 'lm-fitcheck__opt' + (b ? '' : ' lm-fitcheck__opt--unknown');
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', 'false');
        li.dataset.index = i;
        li.textContent = b ? b.n : t.breedUnknownOption;
        if (b) {
          var meta = document.createElement('span');
          meta.className = 'lm-fitcheck__opt-meta';
          meta.textContent = formatRange(b.lo, b.hi, state.units);
          li.appendChild(meta);
        }
        breedList.appendChild(li);
      });
      active = -1;
      breedList.scrollTop = 0;
      breedList.hidden = false;
      breedInput.setAttribute('aria-expanded', 'true');
      breedInput.removeAttribute('aria-activedescendant');
    }

    function closeList() {
      breedList.hidden = true;
      breedInput.setAttribute('aria-expanded', 'false');
      breedInput.removeAttribute('aria-activedescendant');
      active = -1;
    }

    /* Drop a breed choice; its typical height goes with it, a measured one stays. */
    function clearBreed() {
      if (!state.breed && !state.breedUnknown) return;
      state.breed = null;
      state.breedUnknown = false;
      if (state.heightSource === 'breed') {
        shoulderInput.value = '';
        state.heightSource = null;
      }
      renderDog(true);
    }

    function chooseBreed(b) {
      closeList();
      state.breed = b;
      state.breedUnknown = !b;
      breedInput.value = b ? b.n : t.breedUnknownOption;
      if (b && state.heightSource !== 'measured') {
        shoulderInput.value = shoulderValue(breedMid(b));
        state.heightSource = 'breed';
      } else if (!b && state.heightSource === 'breed') {
        shoulderInput.value = '';
        state.heightSource = null;
      }
      if (!b && measureHelp) measureHelp.open = true;
      renderDog();
    }

    if (breedInput && breedList && shoulderInput) {
      on(breedInput, 'input', function () {
        clearBreed();
        openList();
      });
      on(breedInput, 'click', function () {
        if (breedList.hidden) openList(); else closeList();
      });
      on(breedInput, 'keydown', function (e) {
        var open = !breedList.hidden;
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          if (!open) openList();
          var n = shown.length;
          setActive(e.key === 'ArrowDown' ? (active + 1) % n : (active <= 0 ? n - 1 : active - 1));
        } else if (e.key === 'Enter') {
          if (open && active > -1) {
            e.preventDefault();
            chooseBreed(shown[active]);
          }
        } else if (e.key === 'Escape') {
          if (open) {
            e.preventDefault();
            closeList();
          } else if (breedInput.value) {
            e.preventDefault();
            breedInput.value = '';
            clearBreed();
          }
        }
      });
      on(breedInput, 'blur', function () {
        closeList();
        if (state.breed || state.breedUnknown) return;
        var match = findBreed(breeds, breedInput.value);
        if (match) chooseBreed(match);
      });
      /* Keep focus in the input so a tap on an option isn't lost to blur. */
      on(breedList, 'mousedown', function (e) { e.preventDefault(); });
      on(breedList, 'click', function (e) {
        var li = e.target.closest('[role="option"]');
        if (li) chooseBreed(shown[Number(li.dataset.index)]);
      });
    }

    /* ---- phones: bring the result into view ----
       On phones the result card sits below the inputs. Once the shopper is
       done with an input (keyboard closed, or a choice tapped) and there's a
       result, scroll just enough to show the card and the drawing below it
       (or as much of the drawing as fits under the card). Never while typing. */
    var phone = window.matchMedia('(max-width: 749px)');
    var revealTimer = null;
    function revealResult(el) {
      clearTimeout(revealTimer);
      /* The delay lets focus settle when moving field to field, and the
         keyboard finish closing. */
      revealTimer = setTimeout(function () {
        if (!phone.matches || !el || el.dataset.status === 'idle' || el.closest('[hidden]')) return;
        var a = document.activeElement;
        if (a && root.contains(a) && a.matches('input[type="number"], input[type="text"]')) return;
        var header = document.querySelector('.section-header');
        var top = (header ? Math.max(0, header.getBoundingClientRect().bottom) : 0) + 12;
        /* Leave room for the product's sticky add-to-cart bar; this far down
           the page it's showing, or about to. */
        var sticky = document.querySelector('.lm-product__sticky');
        var bottom = window.innerHeight - (sticky ? sticky.offsetHeight : 0) - 12;
        var r = (el.closest('.lm-fitcheck__view') || el).getBoundingClientRect();
        var delta = r.bottom > bottom ? r.bottom - bottom : 0;
        if (r.top - delta < top) delta = r.top - top;
        if (Math.abs(delta) < 8) return;
        window.scrollBy({ top: delta, behavior: reduceMotion ? 'auto' : 'smooth' });
      }, 400);
    }
    function panelResult(target) {
      var panel = target.closest('.lm-fitcheck__panel');
      return panel && panel.querySelector('.lm-fitcheck__result');
    }
    on(root, 'focusout', function (e) {
      if (e.target.matches('[data-lm-fit-input], [data-lm-fit-breed]')) revealResult(panelResult(e.target));
    });
    on(root, 'change', function (e) {
      if (e.target.matches('[data-lm-fit-place], [data-lm-fit-setup], [data-lm-fit-climb], [data-lm-fit-puppy]')) {
        revealResult(panelResult(e.target));
      }
    });

    /* ---- first paint ---- */
    applyUnits(state.units);
    renderSpace(true);
    renderDog(true);

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        if (!entries.some(function (e) { return e.isIntersecting; })) return;
        io.disconnect();
        fitTrack('lm_fit_view', { fit_mode: data.mode });
      }, { threshold: 0.25 });
      io.observe(root);
      cleanups.push(function () { io.disconnect(); });
    }

    instances[root.dataset.sectionId] = function () {
      clearTimeout(liveTimer);
      clearTimeout(trackTimer);
      clearTimeout(revealTimer);
      cleanups.forEach(function (fn) { fn(); });
      delete root.dataset.lmInit;
    };
  }

  function initAll(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('.lm-fitcheck'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var destroy = instances[e.detail.sectionId];
    if (destroy) { destroy(); delete instances[e.detail.sectionId]; }
  });
})(typeof window !== 'undefined' ? window : globalThis);
